import { Link } from 'react-router-dom';
import * as React from 'react';

import NotificationDrawerOnScreen from 'client/components/notification-drawer/NotificationDrawerOnScreen';
import { NotificationsContext } from 'client/components/NotificationsContext';
import styled, { css } from 'styled-components';

const notificationsToggleStyle = css`
  margin: 13px;
  display: block;
  position: relative;
`;

const NotificationsToggleButton = styled.button`
  border: none;
  padding: unset;
  background: none;
  ${notificationsToggleStyle}
`;

const NotificationsToggleLink = styled(Link)`
  ${notificationsToggleStyle}
`;

const NotificationsIcon = styled.img`
  object-fit: contain;
  cursor: pointer;
`;

const NotificationsCount = styled.div`
  position: absolute;
  top: -2px;
  right: -4px;
  height: 14px;
  width: 14px;
  border-radius: 7px;
  background-color: ${({ theme }) => theme.color.headerBackground};
  border: 1px solid ${({ theme }) => theme.color.brandRed};
  font-size: 11px;
  font-weight: bolder;
  line-height: 11px;
  letter-spacing: -0.6px;
  color: ${({ theme }) => theme.color.brandRed};
  text-align: center;
`;

const NotificationsIconCount = ({ notificationsCount }: { notificationsCount: number }) => (
  <>
    <NotificationsIcon
      src={
        notificationsCount
          ? 'https://static.introwise.com/images/notification-icon-active.svg'
          : 'https://static.introwise.com/images/notification-icon.svg'
      }
      alt=""
    />
    {notificationsCount > 0 && <NotificationsCount>{notificationsCount}</NotificationsCount>}
  </>
);

const NotificationsMenu = ({ pathname }: { pathname: string }) => {
  const [isNotificationsDrawerOpen, setIsNotificationsDrawerOpen] = React.useState(false);
  const prevPathname = React.useRef(pathname);
  const drawerRef = React.useRef(null);
  const headerRef = React.useRef(null);

  React.useEffect(() => {
    const clickHandler = (event: MouseEvent) => {
      const drawerElem = drawerRef.current;
      const headerElem = headerRef.current;
      let targetElem = event.target as HTMLElement;
      let clickedOutside = true;

      do {
        if (targetElem === drawerElem || targetElem === headerElem) {
          clickedOutside = false;
          break;
        }
        targetElem = targetElem.parentElement;
      } while (targetElem);
      if (clickedOutside) {
        setIsNotificationsDrawerOpen(false);
      }
    };
    document.addEventListener('click', clickHandler);
    return () => {
      document.removeEventListener('click', clickHandler);
    };
  }, []);

  React.useEffect(() => {
    if (prevPathname.current !== pathname) {
      setIsNotificationsDrawerOpen(false);
    }
    prevPathname.current = pathname;
  }, [pathname]);

  const toggleDrawer = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.preventDefault();
    setIsNotificationsDrawerOpen(!isNotificationsDrawerOpen);
  };

  const { notifications } = React.useContext(NotificationsContext);
  const notificationsCount = notifications && notifications.length ? notifications.length : 0;

  return (
    <div ref={headerRef}>
      <NotificationsToggleButton className="large" onClick={toggleDrawer}>
        <NotificationsIconCount notificationsCount={notificationsCount} />
      </NotificationsToggleButton>
      <NotificationsToggleLink className="small" to="/recent-activity">
        <NotificationsIconCount notificationsCount={notificationsCount} />
      </NotificationsToggleLink>
      {isNotificationsDrawerOpen && <NotificationDrawerOnScreen ref={drawerRef} />}
    </div>
  );
};

export default NotificationsMenu;
